'use client';

import React, { useState, useRef, useEffect } from 'react';

interface OptimizedVideoProps {
  src: string;
  posterSrc?: string;
  alt?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  preload?: 'none' | 'metadata' | 'auto';
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  priority?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
}

/**
 * OptimizedVideo component for better video performance
 * 
 * @example
 * <OptimizedVideo 
 *   src="https://cdn.shopify.com/videos/c/o/v/video-file.mp4"
 *   posterSrc="https://cdn.shopify.com/s/files/1/path/to/poster.jpg"
 *   autoPlay
 *   muted
 *   loop
 *   className="w-full h-full object-cover"
 * />
 */
const OptimizedVideo: React.FC<OptimizedVideoProps> = ({
  src,
  posterSrc,
  alt = 'Video content',
  autoPlay = false,
  muted = true,
  loop = false,
  controls = false,
  className = '',
  preload = 'metadata',
  objectFit = 'cover',
  priority = false,
  onPlay,
  onPause,
  onEnd,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  
  useEffect(() => {
    if (!videoRef.current) return;
    
    const videoElement = videoRef.current;
    
    const handleLoadedData = () => {
      setIsLoaded(true);
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
      if (onPlay) onPlay();
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      if (onPause) onPause();
    };
    
    const handleEnded = () => {
      if (!loop) {
        setIsPlaying(false);
        if (onEnd) onEnd();
      }
    };
    
    // Add event listeners
    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    
    // Clean up
    return () => {
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [loop, onEnd, onPause, onPlay]);
  
  // Determine source type
  let sourceType = 'video/mp4';
  if (src.endsWith('.webm')) sourceType = 'video/webm';
  else if (src.endsWith('.ogv')) sourceType = 'video/ogg';
  
  return (
    <div className={`relative overflow-hidden ${className}`} style={{ objectFit }}>
      <video
        ref={videoRef}
        playsInline
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        controls={controls}
        preload={priority ? 'auto' : preload}
        poster={posterSrc}
        aria-label={alt}
        className="w-full h-full"
        style={{ objectFit }}
      >
        <source src={src} type={sourceType} />
        Your browser does not support the video tag.
      </video>
      
      {!isLoaded && posterSrc && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-white/30 border-t-white/80 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default OptimizedVideo; 